import { useState } from 'react';
import { Globe, Volume2, Database, Fingerprint, Zap, MapPin, ChevronRight, Check } from 'lucide-react';

interface AdvancedSettingsScreenProps {
  onBack: () => void;
}

export function AdvancedSettingsScreen({ onBack }: AdvancedSettingsScreenProps) {
  const [language, setLanguage] = useState('english');
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [vibrationEnabled, setVibrationEnabled] = useState(true);
  const [dataSaver, setDataSaver] = useState(false);
  const [biometricEnabled, setBiometricEnabled] = useState(false);
  const [autoAccept, setAutoAccept] = useState(false);
  const [preferredZones, setPreferredZones] = useState<string[]>(['victoria-island', 'lekki']);

  const languages = [
    { code: 'english', name: 'English', native: 'English' },
    { code: 'yoruba', name: 'Yoruba', native: 'Yorùbá' },
    { code: 'igbo', name: 'Igbo', native: 'Igbo' },
    { code: 'hausa', name: 'Hausa', native: 'Hausa' },
  ];

  const zones = [
    { id: 'kuto', name: 'Kuto' },
    { id: 'ibara', name: 'Ibara' },
    { id: 'oke-ilewo', name: 'Oke-Ilewo' },
    { id: 'adatan', name: 'Adatan' },
    { id: 'isale-igbein', name: 'Isale Igbein' },
    { id: 'funaab', name: 'FUNAAB Area' },
    { id: 'olomore', name: 'Olomore' },
    { id: 'lafenwa', name: 'Lafenwa' },
  ];

  const toggleZone = (zoneId: string) => {
    if (preferredZones.includes(zoneId)) {
      setPreferredZones(preferredZones.filter(z => z !== zoneId));
    } else {
      setPreferredZones([...preferredZones, zoneId]);
    }
  };

  return (
    <div className="absolute inset-0 bg-white flex flex-col">
      {/* Header */}
      <div className="p-8 pt-16 bg-gradient-to-b from-gray-50 to-transparent">
        <button
          onClick={onBack}
          className="mb-6 text-[#E0001B] hover:underline"
        >
          ← Back
        </button>

        <h1 className="text-[28px] text-[#111111] mb-2">
          Advanced Settings
        </h1>
        <p className="text-[#6B6B6B]">
          Customize your app experience
        </p>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto px-8 pb-8">
        {/* Language */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Globe className="w-5 h-5 text-[#E0001B]" />
            <h2 className="text-[18px] font-medium">Language</h2>
          </div>
          <div className="space-y-2">
            {languages.map(lang => (
              <button
                key={lang.code}
                onClick={() => setLanguage(lang.code)}
                className={`w-full p-4 rounded-[12px] border-2 transition-colors flex items-center justify-between ${
                  language === lang.code
                    ? 'bg-[#E0001B]/5 border-[#E0001B]'
                    : 'bg-white border-gray-200 hover:border-gray-300'
                }`}
              >
                <div>
                  <p className="text-[16px] font-medium text-left">{lang.name}</p>
                  <p className="text-[14px] text-[#6B6B6B] text-left">{lang.native}</p>
                </div>
                {language === lang.code && (
                  <Check className="w-5 h-5 text-[#E0001B]" />
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Sound & Notifications */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Volume2 className="w-5 h-5 text-[#E0001B]" />
            <h2 className="text-[18px] font-medium">Sound & Vibration</h2>
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-[12px]">
              <div>
                <p className="text-[16px] font-medium">Notification Sounds</p>
                <p className="text-[14px] text-[#6B6B6B]">Play sound for new jobs</p>
              </div>
              <button
                onClick={() => setSoundEnabled(!soundEnabled)}
                className={`w-12 h-7 rounded-full transition-colors relative ${
                  soundEnabled ? 'bg-[#00B277]' : 'bg-gray-300'
                }`}
              >
                <div
                  className={`absolute w-5 h-5 bg-white rounded-full top-1 transition-all ${
                    soundEnabled ? 'right-1' : 'left-1'
                  }`}
                />
              </button>
            </div>

            <div className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-[12px]">
              <div>
                <p className="text-[16px] font-medium">Vibration</p>
                <p className="text-[14px] text-[#6B6B6B]">Vibrate for notifications</p>
              </div>
              <button
                onClick={() => setVibrationEnabled(!vibrationEnabled)}
                className={`w-12 h-7 rounded-full transition-colors relative ${
                  vibrationEnabled ? 'bg-[#00B277]' : 'bg-gray-300'
                }`}
              >
                <div
                  className={`absolute w-5 h-5 bg-white rounded-full top-1 transition-all ${
                    vibrationEnabled ? 'right-1' : 'left-1'
                  }`}
                />
              </button>
            </div>
          </div>
        </div>

        {/* Data & Performance */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Database className="w-5 h-5 text-[#E0001B]" />
            <h2 className="text-[18px] font-medium">Data & Performance</h2>
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-[12px]">
              <div>
                <p className="text-[16px] font-medium">Data Saver Mode</p>
                <p className="text-[14px] text-[#6B6B6B]">Reduce data usage</p>
              </div>
              <button
                onClick={() => setDataSaver(!dataSaver)}
                className={`w-12 h-7 rounded-full transition-colors relative ${
                  dataSaver ? 'bg-[#00B277]' : 'bg-gray-300'
                }`}
              >
                <div
                  className={`absolute w-5 h-5 bg-white rounded-full top-1 transition-all ${
                    dataSaver ? 'right-1' : 'left-1'
                  }`}
                />
              </button>
            </div>
          </div>
        </div>

        {/* Security */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Fingerprint className="w-5 h-5 text-[#E0001B]" />
            <h2 className="text-[18px] font-medium">Security</h2>
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-[12px]">
              <div>
                <p className="text-[16px] font-medium">Biometric Login</p>
                <p className="text-[14px] text-[#6B6B6B]">Use fingerprint or face ID</p>
              </div>
              <button
                onClick={() => setBiometricEnabled(!biometricEnabled)}
                className={`w-12 h-7 rounded-full transition-colors relative ${
                  biometricEnabled ? 'bg-[#00B277]' : 'bg-gray-300'
                }`}
              >
                <div
                  className={`absolute w-5 h-5 bg-white rounded-full top-1 transition-all ${
                    biometricEnabled ? 'right-1' : 'left-1'
                  }`}
                />
              </button>
            </div>
          </div>
        </div>

        {/* Job Preferences */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Zap className="w-5 h-5 text-[#E0001B]" />
            <h2 className="text-[18px] font-medium">Job Preferences</h2>
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-[12px]">
              <div>
                <p className="text-[16px] font-medium">Auto-Accept Jobs</p>
                <p className="text-[14px] text-[#6B6B6B]">Automatically accept nearby jobs</p>
              </div>
              <button
                onClick={() => setAutoAccept(!autoAccept)}
                className={`w-12 h-7 rounded-full transition-colors relative ${
                  autoAccept ? 'bg-[#00B277]' : 'bg-gray-300'
                }`}
              >
                <div
                  className={`absolute w-5 h-5 bg-white rounded-full top-1 transition-all ${
                    autoAccept ? 'right-1' : 'left-1'
                  }`}
                />
              </button>
            </div>
          </div>
        </div>

        {/* Preferred Delivery Zones */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-4">
            <MapPin className="w-5 h-5 text-[#E0001B]" />
            <h2 className="text-[18px] font-medium">Preferred Delivery Zones</h2>
          </div>
          <p className="text-[14px] text-[#6B6B6B] mb-4">
            Select areas where you prefer to deliver
          </p>
          <div className="grid grid-cols-2 gap-3">
            {zones.map(zone => (
              <button
                key={zone.id}
                onClick={() => toggleZone(zone.id)}
                className={`p-4 rounded-[12px] border-2 transition-colors ${
                  preferredZones.includes(zone.id)
                    ? 'bg-[#E0001B]/5 border-[#E0001B]'
                    : 'bg-white border-gray-200'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-[14px] font-medium">{zone.name}</span>
                  {preferredZones.includes(zone.id) && (
                    <Check className="w-5 h-5 text-[#E0001B]" />
                  )}
                </div>
              </button>
            ))}
          </div>
          <p className="text-[12px] text-[#6B6B6B] mt-3">
            * You'll still receive jobs from other areas, but prioritize these zones
          </p>
        </div>

        {/* Save Button */}
        <button
          onClick={() => {
            alert('Settings saved successfully!');
            onBack();
          }}
          className="w-full h-12 rounded-[12px] bg-[#00B277] text-white hover:bg-[#009963] transition-colors shadow-sm flex items-center justify-center gap-2 font-medium"
        >
          <Check className="w-5 h-5" />
          Save Settings
        </button>
      </div>
    </div>
  );
}
